// src/components/pages/message/timecapsule/components/MediaPreview.js
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Box, CardMedia, CircularProgress } from "@mui/material";
import { alpha } from "@mui/material/styles";

const IMAGE_PREVIEW_TIMEOUT_MS = 12000;
const VIDEO_PREVIEW_TIMEOUT_MS = 20000;
const VIDEO_BLOB_PREVIEW_TIMEOUT_MS = 45000;
const GENERATED_POSTER_TIMEOUT_MS = 6000;
const VIDEO_POSTER_CACHE = new Map();

function firstNonEmptyString(...values) {
  return values.map((value) => String(value || "").trim()).find(Boolean) || "";
}

export default function MediaPreview({
  mediaFile,
  outerSx,
  borderRadius = "20px",
  disableInteraction = false,
  fillContainer = false,
  onAspectRatioChange,
}) {
  const [imageAspectRatio, setImageAspectRatio] = useState(null);
  const [videoAspectRatio, setVideoAspectRatio] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [generatedVideoPoster, setGeneratedVideoPoster] = useState("");
  const loadTimeoutRef = useRef(null);
  const mediaUrl = String(mediaFile?.url || "").trim();
  const isBlobUrl = mediaUrl.startsWith("blob:");
  const isRemoteUrl = /^https?:\/\//i.test(mediaUrl);

  const clearLoadWatchdog = useCallback(() => {
    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = null;
    }
  }, []);

  const isImage = useMemo(
    () => !!mediaFile?.type?.startsWith("image"),
    [mediaFile?.type]
  );
  const explicitVideoPoster = useMemo(
    () =>
      firstNonEmptyString(
        mediaFile?.sessionPreviewUrl,
        mediaFile?.thumbnailUrl,
        mediaFile?.posterUrl
      ),
    [mediaFile?.posterUrl, mediaFile?.sessionPreviewUrl, mediaFile?.thumbnailUrl]
  );
  const videoPoster = !isImage
    ? (
        explicitVideoPoster
        || generatedVideoPoster
      )
    : "";
  const shouldUseStaticVideoPreview = !isImage && disableInteraction && Boolean(explicitVideoPoster || isRemoteUrl);

  useEffect(() => {
    if (!mediaFile?.url) return;
    const mediaUrl = String(mediaFile.url || "").trim();
    clearLoadWatchdog();
    setIsLoading(true);
    setHasError(false);
    setImageAspectRatio(null);
    setVideoAspectRatio(null);
    setGeneratedVideoPoster(VIDEO_POSTER_CACHE.get(mediaUrl) || "");
    // Prevent infinite spinner when remote media never resolves.
    loadTimeoutRef.current = setTimeout(() => {
      setIsLoading(false);
      setHasError(true);
    }, IMAGE_PREVIEW_TIMEOUT_MS);

    return () => clearLoadWatchdog();
  }, [mediaFile?.url, clearLoadWatchdog]);

  useEffect(() => {
    if (isImage || !mediaFile?.url) return;

    if (explicitVideoPoster || (disableInteraction && isRemoteUrl)) {
      setGeneratedVideoPoster("");
      return;
    }

    const mediaUrl = String(mediaFile.url || "").trim();
    if (!mediaUrl) return;

    const cachedPoster = VIDEO_POSTER_CACHE.get(mediaUrl);
    if (cachedPoster) {
      setGeneratedVideoPoster(cachedPoster);
      return;
    }

    let canceled = false;
    const probe = document.createElement("video");
    probe.preload = "metadata";
    probe.muted = true;
    probe.playsInline = true;
    probe.crossOrigin = "anonymous";

    const cleanup = () => {
      probe.onloadedmetadata = null;
      probe.onseeked = null;
      probe.onerror = null;
      probe.pause?.();
      probe.removeAttribute("src");
      probe.load();
    };

    let didFinalize = false;
    const timeoutId = window.setTimeout(() => finalize(""), GENERATED_POSTER_TIMEOUT_MS);

    const finalize = (poster = "") => {
      if (didFinalize) return;
      didFinalize = true;
      clearTimeout(timeoutId);
      if (canceled) return;
      if (poster) {
        VIDEO_POSTER_CACHE.set(mediaUrl, poster);
        setGeneratedVideoPoster(poster);
      } else {
        setGeneratedVideoPoster("");
      }
      cleanup();
    };

    const captureFrame = () => {
      const width = Number(probe.videoWidth || 0);
      const height = Number(probe.videoHeight || 0);
      if (!width || !height) {
        finalize("");
        return;
      }

      try {
        const maxDimension = 1200;
        const scale = Math.min(1, maxDimension / Math.max(width, height));
        const targetWidth = Math.max(1, Math.round(width * scale));
        const targetHeight = Math.max(1, Math.round(height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          finalize("");
          return;
        }
        ctx.drawImage(probe, 0, 0, targetWidth, targetHeight);
        const dataUrl = canvas.toDataURL("image/webp", 0.72) || "";
        finalize(dataUrl);
      } catch {
        finalize("");
      }
    };

    probe.onloadedmetadata = () => {
      const duration = Number(probe.duration || 0);
      if (duration > 0.2) {
        try {
          probe.currentTime = Math.min(0.25, Math.max(0, duration - 0.05));
          return;
        } catch {
          // fall through to immediate capture
        }
      }
      captureFrame();
    };

    probe.onseeked = () => captureFrame();
    probe.onerror = () => finalize("");
    probe.src = mediaUrl;
    probe.load();

    return () => {
      canceled = true;
      clearTimeout(timeoutId);
      cleanup();
    };
  }, [disableInteraction, explicitVideoPoster, isImage, isRemoteUrl, mediaFile?.url]);

  useEffect(() => {
    if (!isImage || !mediaFile?.url) return;
    let canceled = false;
    const probe = new Image();
    probe.onload = () => {
      if (canceled) return;
      if (probe.naturalWidth && probe.naturalHeight) {
        const nextAspectRatio = `${probe.naturalWidth} / ${probe.naturalHeight}`;
        setImageAspectRatio(nextAspectRatio);
        if (typeof onAspectRatioChange === "function") {
          onAspectRatioChange(nextAspectRatio);
        }
      }
      clearLoadWatchdog();
      setIsLoading(false);
      setHasError(false);
    };
    probe.onerror = () => {
      if (canceled) return;
      clearLoadWatchdog();
      setIsLoading(false);
      setHasError(true);
    };
    probe.src = mediaFile.url;
    if (probe.complete) {
      if (probe.naturalWidth && probe.naturalHeight) {
        const nextAspectRatio = `${probe.naturalWidth} / ${probe.naturalHeight}`;
        setImageAspectRatio(nextAspectRatio);
        if (typeof onAspectRatioChange === "function") {
          onAspectRatioChange(nextAspectRatio);
        }
      }
      clearLoadWatchdog();
      setIsLoading(false);
      setHasError(false);
    }
    return () => {
      canceled = true;
      probe.onload = null;
      probe.onerror = null;
    };
  }, [isImage, mediaFile?.url, clearLoadWatchdog, onAspectRatioChange]);

  useEffect(() => {
    if (isImage || !shouldUseStaticVideoPreview || videoPoster) return;
    clearLoadWatchdog();
    setIsLoading(false);
    setHasError(true);
  }, [clearLoadWatchdog, isImage, shouldUseStaticVideoPreview, videoPoster]);

  useEffect(() => () => clearLoadWatchdog(), [clearLoadWatchdog]);

  if (!mediaFile) return null;

  const resolvedAspect = imageAspectRatio || videoAspectRatio || "4 / 3";

  const Overlay = (
    <Box
      sx={(theme) => ({
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius,
        backgroundColor: alpha(theme.palette.common.black, 0.18),
        backdropFilter: "blur(1px)",
        zIndex: 2,
        pointerEvents: "none",
      })}
    >
      <CircularProgress />
    </Box>
  );

  const ErrorOverlay = (
    <Box
      sx={(theme) => ({
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius,
        backgroundColor: alpha(theme.palette.common.black, 0.45),
        zIndex: 3,
        px: 2,
        textAlign: "center",
        color: theme.palette.common.white,
        fontWeight: 700,
        pointerEvents: "none",
      })}
    >
      Preview unavailable
    </Box>
  );

  return (
    <Box sx={{ mb: 3, mt: 2, ...(outerSx || {}) }}>
      {isImage ? (
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: fillContainer ? "100%" : "auto",
            borderRadius,
            overflow: "hidden",
          }}
        >
          {(isLoading || hasError) && (
            <Box
              sx={(theme) => ({
                width: "100%",
                height: fillContainer ? "100%" : "auto",
                aspectRatio: fillContainer ? undefined : resolvedAspect,
                backgroundColor: alpha(theme.palette.common.black, 0.06),
              })}
            />
          )}

          {isLoading && Overlay}
          {hasError && ErrorOverlay}

          <CardMedia
            component="img"
            image={mediaFile.url}
            alt="preview"
            sx={{
              width: "100%",
              height: fillContainer ? "100%" : "auto",
              objectFit: "contain",
              backgroundColor: "transparent",
              display: hasError ? "none" : "block",
              borderRadius,
            }}
            onLoad={() => setIsLoading(false)}
            onLoadCapture={clearLoadWatchdog}
            onError={() => {
              clearLoadWatchdog();
              setIsLoading(false);
              setHasError(true);
            }}
          />
        </Box>
      ) : (
        <Box
          sx={{
            position: "relative",
            width: "100%",
            borderRadius,
            overflow: "hidden",
            aspectRatio: resolvedAspect,
            backgroundColor: "common.black",
            backgroundImage: videoPoster ? `url(${videoPoster})` : "none",
            backgroundSize: "contain",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        >
          {isLoading && Overlay}
          {hasError && ErrorOverlay}

          {shouldUseStaticVideoPreview && videoPoster ? (
            <CardMedia
              component="img"
              image={videoPoster}
              alt="video preview"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                display: hasError ? "none" : "block",
                pointerEvents: "none",
              }}
              onLoad={(event) => {
                const image = event.currentTarget;
                if (image?.naturalWidth && image?.naturalHeight) {
                  const nextAspectRatio = `${image.naturalWidth} / ${image.naturalHeight}`;
                  setVideoAspectRatio(nextAspectRatio);
                  if (typeof onAspectRatioChange === "function") {
                    onAspectRatioChange(nextAspectRatio);
                  }
                }
                clearLoadWatchdog();
                setIsLoading(false);
                setHasError(false);
              }}
              onError={() => {
                clearLoadWatchdog();
                setIsLoading(false);
                setHasError(true);
              }}
            />
          ) : shouldUseStaticVideoPreview ? null : (
            <video
              src={mediaFile.url}
              poster={videoPoster || undefined}
              controls={!hasError && !disableInteraction}
              playsInline
              preload="auto"
              style={{
                width: "100%",
                height: "100%",
                display: hasError ? "none" : "block",
                objectFit: "contain",
                pointerEvents: disableInteraction ? "none" : "auto",
              }}
              onLoadStart={() => {
                clearLoadWatchdog();
                setIsLoading(true);
                setHasError(false);
                loadTimeoutRef.current = setTimeout(() => {
                  setIsLoading(false);
                  setHasError(true);
                }, isBlobUrl ? VIDEO_BLOB_PREVIEW_TIMEOUT_MS : VIDEO_PREVIEW_TIMEOUT_MS);
              }}
              onLoadedMetadata={(e) => {
                const v = e.currentTarget;
                if (v?.videoWidth && v?.videoHeight) {
                  const nextAspectRatio = `${v.videoWidth} / ${v.videoHeight}`;
                  setVideoAspectRatio(nextAspectRatio);
                  if (typeof onAspectRatioChange === "function") {
                    onAspectRatioChange(nextAspectRatio);
                  }
                }
                // iOS Safari often reaches metadata before canplay/loadeddata for blob recordings.
                // Showing the preview at this point prevents false "Preview unavailable" states.
                clearLoadWatchdog();
                setIsLoading(false);
                setHasError(false);
              }}
              onLoadedData={() => {
                clearLoadWatchdog();
                setIsLoading(false);
              }}
              onCanPlay={() => {
                clearLoadWatchdog();
                setIsLoading(false);
              }}
              onError={() => {
                clearLoadWatchdog();
                setIsLoading(false);
                setHasError(true);
              }}
            />
          )}
        </Box>
      )}
    </Box>
  );
}
